import actions from './actions';
import { Moment } from 'moment';
import { ComponentsActionTypes } from './types';
import * as service from '../../service/components';
import { displayNotification } from '../notification/actions';
import { NotificationActionTypes } from '../notification/types';
import {
  Section as SectionType,
  ComponentSections as ComponentSectionType,
  LocalSection,
  ComponentSectionDoc,
  Component as ComponentType,
} from '../../service/components/types';
import {
  SubmissionWindow,
  ComponentSeries,
  Component,
  ExamDate,
  ReportType,
} from '@cambridgeassessment/lib-ee-commons';
import { RootState } from '..';
import _ from 'lodash';
import { Section } from '../../types/Components';
import { buildReportName, downloadHandler } from '../../util/downloadHandler';

type Dispatch = (action: ComponentsActionTypes | NotificationActionTypes) => void;
type GetState = () => RootState;

export const getComponentByIdentifiers =
  (businessUnit: string, assessment: string, componentNumber: string, qualificationId?: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsLoading(true));
      const result = await service.getComponentByIdentifiers(
        businessUnit,
        assessment,
        componentNumber,
        qualificationId,
      );
      dispatch(actions.setFetchedComponent(result));
    } catch (err) {
      dispatch(actions.setError());
      dispatch(displayNotification('No results were found.'));
    } finally {
      dispatch(actions.setIsLoading(false));
    }
  };

export const getComponent =
  (componentId: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsLoading(true));
      const result = await service.getComponentData(componentId);
      dispatch(actions.setComponent(componentId, result));
    } catch (err) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to get Component data'));
    } finally {
      dispatch(actions.setIsLoading(false));
    }
  };

export const getComponentWithEntriesCheck =
  (businessUnitId: string, seriesIds: number[], componentId: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsLoading(true));
      const result = await service.getComponentWithEntriesCheck(businessUnitId, seriesIds, componentId);
      dispatch(actions.setComponent(componentId, result));
    } catch (err) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to get Component data'));
    } finally {
      dispatch(actions.setIsLoading(false));
    }
  };

export const getComponents =
  (businessUnit: string, qualificationId: number, assessment: string, assessmentVersion: number) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.getComponents());
      const result = await service.getComponentsData(businessUnit, qualificationId, assessment, assessmentVersion);
      dispatch(actions.setComponents(result));
    } catch (err) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to get Components data'));
    }
  };

export const getComponentsWithEntriesCheck =
  (businessUnit: string, seriesIds: number[], qualificationId: number, assessment: string, assessmentVersion: number) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.getComponents());
      const result = await service.getComponentsWithEntriesCheck(
        businessUnit,
        seriesIds,
        qualificationId,
        assessment,
        assessmentVersion,
      );
      dispatch(actions.setComponents(result));
    } catch (err) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to get Components data'));
    }
  };

export const updateAdditionalFileTypes =
  (componentId: ComponentType['_id'], value: ComponentType['additionalFileTypes']) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const components = [...(getState().components.components || [])];
      const component = components.find((item) => item._id === componentId);
      if (component) {
        const updatedFileTypes = _.set(component, 'additionalFileTypes', value);
        dispatch(actions.setComponent(componentId, updatedFileTypes));
        await service.updateAdditionalFileTypes(componentId, value);
      }
    } catch (err) {
      dispatch(displayNotification('Failed to update Additional file types'));
    }
  };

export const updateComponent =
  (componentId: ComponentType['_id'], Component: ComponentType, field: keyof ComponentType) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const result = await service.updateComponent(componentId, Component[field], field);
      dispatch(actions.setComponent(componentId, result));
    } catch (err) {
      dispatch(displayNotification('Failed to update Component'));
    }
  };

export const updateSeriesWhenSwitchingToggleOn =
  (componentId: ComponentType['_id']) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      await service.updateSeriesStartDateByComponentSeries(componentId);
    } catch {
      dispatch(displayNotification('Failed to update series start date'));
    }
  };

export const createSection =
  (componentId: string, Section: LocalSection) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const result = await service.createSection(componentId, Section);
      dispatch(actions.setComponent(componentId, result));
    } catch (err) {
      dispatch(displayNotification('Failed to create Section'));
    }
  };

export const createComponentSection =
  (componentId: string, ComponentSection: ComponentSectionDoc) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const result = await service.createComponentSection(componentId, ComponentSection);
      dispatch(actions.setComponentSection(componentId, result));
    } catch (err) {
      dispatch(displayNotification('Failed to create Document Section'));
    }
  };

export const updateSection =
  (componentId: string, Section: SectionType, field: keyof SectionType, id: number) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const components = [...(getState().components.components || [])];
      const component = components.find((item: Component) => item._id === componentId);

      if (!component) {
        return;
      }

      const idToUpdate = component?.sections.findIndex((section) => section.id === id);
      const section = component?.sections.find((section) => section.id === id);

      if (!section) {
        return;
      }

      const updatedSection: Section = { ...section, [field]: Section[field] };
      component?.sections.splice(idToUpdate, 1, updatedSection);

      dispatch(actions.setComponent(componentId, component));
      await service.updateSection(componentId, Section[field], field, id);
    } catch (err) {
      dispatch(displayNotification('Failed to update Section'));
    }
  };

export const updateComponentSection =
  (componentId: string, ComponentSection: ComponentSectionType, field: keyof ComponentSectionType, id: number) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const components = [...(getState().components.components || [])];
      const component = components.find((item: Component) => item._id === componentId);
      if (component) {
        const sectionIndex = component.componentSections.findIndex((section) => section.id === id);
        const updatedComponent = _.set(
          component,
          `componentSections[${sectionIndex}].${field}`,
          ComponentSection[field],
        );
        dispatch(actions.setComponentSection(componentId, updatedComponent));
        await service.updateComponentSection(componentId, ComponentSection[field], field, id);
      }
    } catch (err) {
      dispatch(displayNotification('Failed to update Document section'));
    }
  };
export const updateSeries =
  (
    componentId: string,
    value: string | number | Moment | undefined | SubmissionWindow | ExamDate[] | boolean,
    field:
      | keyof ComponentSeries
      | keyof SubmissionWindow
      | 'marksSubmissionWindowStartDate'
      | 'marksSubmissionWindowEndDate'
      | 'marksSubmissionWindowPersistStartDate'
      | 'marksSubmissionWindowPersistEndDate',
    id: number,
  ) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const component = await service.updateSeries(componentId, value, field, id);

      if (field === 'marksSubmissionWindow') {
        const { defaultStartDate } = await service.setDefaultSalgoStartDate(componentId, id);
        const componentSeriesIndex = component.series.findIndex((serie) => serie.id === id);
        component.series[componentSeriesIndex].salgoStartDate = defaultStartDate;
      }

      dispatch(actions.setComponent(componentId, component));
    } catch (err) {
      dispatch(displayNotification('Failed to update Series'));
    }
  };

export const addExamDate =
  (componentId: string, seriesId: ComponentSeries['id'], examDate: ExamDate) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const result = await service.addExamDate(componentId, seriesId, examDate);
      dispatch(actions.setComponent(componentId, result));
    } catch (err) {
      dispatch(displayNotification('Failed to add Exam date'));
    }
  };

export const updateToBeConfirmed =
  (componentId: string | undefined, newValue: boolean) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      await service.updateComponent(componentId, newValue, 'toBeConfirmed');
      window.location.pathname = `/components/configure-component/${componentId}`;
    } catch {
      dispatch(displayNotification('Failed to update component'));
    }
  };

export const updatePlagiarismFlag =
  (componentId: string, Section: SectionType, field: keyof SectionType, id: number) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const result = await service.updatePlagiarismFlag(componentId, Section[field], field, id);
      dispatch(actions.setComponent(componentId, result));
    } catch (err) {
      dispatch(displayNotification('Failed to check section for plagiarism'));
    }
  };

export const deleteSection =
  (componentId: string, id: number) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.deleteSection(componentId, id));
      await service.deleteSection(componentId, id);
    } catch (err) {
      dispatch(displayNotification('Failed to remove Section'));
    }
  };

export const deleteComponentSection =
  (componentId: string, id: number) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.deleteComponentSection(componentId, id));
      await service.deleteComponentSection(componentId, id);
    } catch (err) {
      dispatch(displayNotification('Failed to remove Document Section'));
    }
  };

export const deleteComponentSections =
  (componentId: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.deleteComponentSections(componentId));
      await service.deleteComponentSection(componentId);
    } catch (err) {
      dispatch(displayNotification('Failed to remove Document Sections'));
    }
  };

export const deleteSeriesSubmissionWindow =
  (componentId: string, seriesId: number, submissionWindowField: 'submissionWindow' | 'marksSubmissionWindow') =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      await service.deleteSeriesSubmissionWindow(componentId, seriesId, submissionWindowField);
      dispatch(actions.deleteSeriesSubmissionWindow(componentId, seriesId, submissionWindowField));
    } catch (err) {
      dispatch(displayNotification('Failed to remove Series'));
    }
  };

export const createDigitallyReceivedFlag =
  (componentId: string, SectionData: LocalSection) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const component = await service.createDigitallyReceivedFlag(componentId, SectionData);
      dispatch(actions.addDigitallyReceived(componentId, component.sections[0]));
    } catch (err) {
      dispatch(displayNotification('Failed to create digitally received flag'));
    }
  };

export const deleteDigitallyReceivedFlag =
  (componentId: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      await service.deleteDigitallyReceivedFlag(componentId);
      dispatch(actions.deleteAllSections(componentId));
    } catch (err) {
      dispatch(displayNotification('Failed to delete digitally received flag'));
    }
  };

export const getComponentRulesReport =
  (reportType: ReportType) =>
  async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    try {
      const { businessUnit } = getState().userData;
      if (!businessUnit) {
        throw new Error('BusinessId is not assigned');
      }

      dispatch(actions.setIsReportBeingGenerated(true));
      const data = await service.downloadComponentRulesReport(businessUnit.id);
      const reportName = buildReportName(reportType);
      downloadHandler(data, reportName);
    } catch {
      dispatch(displayNotification('Failed to download component rules report'));
    } finally {
      dispatch(actions.setIsReportBeingGenerated(false));
    }
  };

export default {
  getComponent,
  getComponents,
  getComponentWithEntriesCheck,
  getComponentsWithEntriesCheck,
  getComponentByIdentifiers,
  createSection,
  deleteComponentSection,
  updateSection,
  updateComponentSection,
  deleteSeriesSubmissionWindow,
  updateSeries,
  deleteSection,
  createDigitallyReceivedFlag,
  deleteDigitallyReceivedFlag,
  updateAdditionalFileTypes,
  updatePlagiarismFlag,
  updateComponent,
  createComponentSection,
  getComponentRulesReport,
  updateToBeConfirmed,
};
